import React, { useState } from "react";
import { Box, HStack } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { Dropdown } from "components/ds";

interface ReviewFiltersProps {
  onFilterChange?: (filters: any) => void;
}

type Option = { value: string; label: string };

// A ds split-pill selector backed by a lightweight custom menu (the ds Dropdown
// is presentational, so the consumer owns open/close + the option list).
const FilterSelect = ({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Option[];
  onChange: (value: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value)?.label ?? options[0]?.label;
  return (
    <Box position="relative">
      <Dropdown
        label={label}
        value={current}
        size="sm"
        open={open}
        onClick={() => setOpen((o) => !o)}
      />
      {open && (
        <>
          <Box position="fixed" inset={0} zIndex={9} onClick={() => setOpen(false)} />
          <Box
            position="absolute"
            top="calc(100% + 4px)"
            right={0}
            minW="188px"
            zIndex={10}
            bg="bg.container"
            borderRadius="component.lg"
            borderWidth="1px"
            borderStyle="solid"
            borderColor="border.default"
            boxShadow="overlay"
            py="2xs"
            overflow="hidden"
          >
            {options.map((o) => (
              <Box
                key={o.value}
                as="button"
                type="button"
                display="block"
                width="full"
                textAlign="left"
                px="md"
                py="sm"
                fontSize="smd"
                fontWeight={o.value === value ? 600 : 500}
                color={o.value === value ? "text.body.azurite" : "text.body.primary"}
                bg="transparent"
                _hover={{ bg: "bg.hover" }}
                onClick={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              >
                {o.label}
              </Box>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

/**
 * ReviewFilters — the drawer's sort/group control (PLO-4803). Two ds split-pill
 * selectors on the right of the filter row. State round-trips through the URL
 * (`review_sort_by` / `review_group_by`), unchanged from the previous version.
 */
const ReviewFilters: React.FC<ReviewFiltersProps> = ({ onFilterChange }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const currentSort = searchParams.get("review_sort_by") || "name";
  const currentGroupBy = searchParams.get("review_group_by") || "none";

  const sortOptions: Option[] = [
    { value: "name", label: "Name" },
    { value: "requires_review", label: "Accounts to review" },
    { value: "completed", label: "Completed accounts" },
  ];

  const groupByOptions: Option[] = [
    { value: "none", label: "None" },
    { value: "status", label: "Status" },
  ];

  const updateFilters = (updates: { sort?: string; groupBy?: string }) => {
    const newParams = new URLSearchParams(searchParams);

    const sortValue = updates.sort !== undefined ? updates.sort : currentSort;
    newParams.set("review_sort_by", sortValue);

    const groupByValue =
      updates.groupBy !== undefined ? updates.groupBy : currentGroupBy;
    if (groupByValue === "none") {
      newParams.delete("review_group_by");
    } else {
      newParams.set("review_group_by", groupByValue);
    }

    setSearchParams(newParams);

    if (onFilterChange) {
      const filters: any = { sort_by: sortValue, sort_direction: "desc" };
      if (groupByValue !== "none") {
        filters.group_by = groupByValue;
      }
      onFilterChange(filters);
    }
  };

  return (
    <HStack spacing="sm" align="center">
      <FilterSelect
        label="Sort by"
        value={currentSort}
        options={sortOptions}
        onChange={(v) => updateFilters({ sort: v })}
      />
      <FilterSelect
        label="Group by"
        value={currentGroupBy}
        options={groupByOptions}
        onChange={(v) => updateFilters({ groupBy: v })}
      />
    </HStack>
  );
};

export default ReviewFilters;
